import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLocation } from 'react-router-dom';

const Contact = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const selectedService = params.get('service') || '';

  return (
    <>
      <Helmet>
        <title>Contact TiLimousine | Luxury Chauffeur Service</title>
        <meta name="description" content="Get in touch with TiLimousine for your luxury transportation needs. Book your premium chauffeur service via phone, email, or WhatsApp." />
        <link rel="canonical" href="https://tilimousine.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact TiLimousine",
            "description": "Contact TiLimousine for premium chauffeur services",
            "url": "https://tilimousine.com/contact",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-857-312-3332",
              "contactType": "customer service",
              "email": "contact@tilimousine.com",
              "availableLanguage": ["English", "Arabic"]
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black">
        <Navbar />
        <main className="container mx-auto px-4 py-20">
          <h1 className="text-4xl md:text-5xl font-cinzel text-gold text-center mb-12">
            Contact TiLimousine for Premium Chauffeur Services
          </h1>

          {selectedService && (
            <div className="mb-8 text-center">
              <span className="inline-block bg-gold text-black px-4 py-2 rounded-full font-semibold">
                Booking Inquiry: {selectedService}
              </span>
            </div>
          )}

          {selectedService && (
            <section className="mt-8 text-white">
              <h2 className="text-2xl font-cinzel text-gold mb-6">Book Your Service</h2>
              <form onSubmit={(e) => { e.preventDefault(); /* wire your email logic here */ }} className="space-y-4">
                {/* Hidden input for the service type */}
                <input type="hidden" name="service" value={selectedService} />
                {/* Visible, read-only field for confirmation */}
                <div className="mb-4">
                  <label className="block text-gold mb-1">Selected Service:</label>
                  <input type="text" name="service_display" value={selectedService} readOnly className="w-full bg-gray-900 border border-gold/20 rounded px-4 py-2 text-white" />
                </div>
                {/* Add your other booking fields here, e.g. name, email, message, etc. */}
                <button type="submit" className="inline-block bg-gold text-black px-4 py-2 rounded-full hover:bg-gold/80 transition-colors font-semibold">Submit Booking</button>
              </form>
            </section>
          )}

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <section className="text-white">
                <h2 className="text-2xl font-cinzel text-gold mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-cinzel text-gold mb-3">Phone</h3>
                    <a 
                      href="tel:+18573123332" 
                      className="text-white hover:text-gold text-lg block mb-2"
                    >
                      +1 (857) 312-3332
                    </a>
                    <p className="text-gray-400">Available 24/7 for your convenience</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-cinzel text-gold mb-3">Email</h3>
                    <a 
                      href="mailto:contact@tilimousine.com" 
                      className="text-white hover:text-gold text-lg block mb-2"
                    >
                      Contact@tilimousine.com
                    </a>
                    <p className="text-gray-400">We'll respond within 24 hours</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-cinzel text-gold mb-3">Social Media</h3>
                    <div className="flex gap-4">
                      <a 
                        href="https://www.instagram.com/tilimousine?igsh=ejNrempiZm5raDdm" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-white hover:text-gold transition-colors"
                      >
                        Instagram
                      </a>
                      <a 
                        href="https://www.facebook.com/share/16QvJTW2BH/?mibextid=wwXIfr" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-white hover:text-gold transition-colors"
                      >
                        Facebook
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              <section className="text-white">
                <h2 className="text-2xl font-cinzel text-gold mb-6">Quick Contact</h2>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <a 
                      href="sms:+18573123332" 
                      className="inline-flex items-center gap-2 text-white hover:text-gold transition-colors border border-gold/20 px-6 py-3 rounded-full hover:border-gold"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <span>SMS Us</span>
                    </a>
                  </div>

                  <div className="flex items-center gap-4">
                    <a 
                      href="https://wa.me/18573123332" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 text-white hover:text-gold transition-colors border border-gold/20 px-6 py-3 rounded-full hover:border-gold"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                      </svg>
                      <span>WhatsApp Us</span>
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Contact; 